import type {
  ThemeCapability,
  ThemeCapabilityState,
  ThemeLifecycle,
} from '@ffs-studio/protocol';
import { CheckCircle2, CircleHelp, Play, RefreshCw, Save, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  loadThemeDiscovery,
  loadHalloweenGhostCamera,
  testHalloweenWelcome,
  updateHalloweenGhostCamera,
  type HalloweenGhostCameraSnapshot,
  type HalloweenGhostPreset,
  type ThemeDiscoveryTheme,
  type ThemeStreamLifecycleSnapshot,
} from './themes-api';
import { loadProduction, type ProductionSnapshot } from './stream-api';
import './workspace.css';
import './themes.css';

const CAPABILITIES: Array<{ id: ThemeCapability; label: string }> = [
  { id: 'startup', label: 'Startup' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'welcomes', label: 'Welcomes' },
  { id: 'wam', label: 'WhatsAMusic' },
  { id: 'viewerSounds', label: 'Viewer sounds' },
  { id: 'privateChatSounds', label: 'Private chat sounds' },
  { id: 'achievements', label: 'Achievements' },
];

function lifecycleLabel(value: ThemeLifecycle): string {
  if (value === 'ready') return 'Ready';
  if (value === 'development') return 'Development';
  return 'Reserved';
}

function capabilityLabel(value: ThemeCapabilityState): string {
  if (value === 'available') return 'Available';
  if (value === 'external') return 'External';
  if (value === 'unsupported') return 'Unsupported';
  return 'Reserved';
}

function releaseLabel(lifecycle: ThemeStreamLifecycleSnapshot): string {
  const delaySeconds = lifecycle.welcomeReleaseDelayMs / 1000;
  return lifecycle.welcomeReleaseRule === 'enter-main'
    ? `${delaySeconds}s after main scene`
    : `${delaySeconds}s after leaving startup`;
}

function ScenePresence({
  sceneName,
  production,
}: {
  sceneName: string | null | undefined;
  production: ProductionSnapshot | null;
}) {
  if (!sceneName) return <span className="ffs-theme-scene-state neutral"><CircleHelp size={11} /> Not packaged</span>;
  if (!production || production.obs.status !== 'online') {
    return <span className="ffs-theme-scene-state neutral"><CircleHelp size={11} /> {sceneName}</span>;
  }
  const found = production.obs.scenes.includes(sceneName);
  return (
    <span className={`ffs-theme-scene-state ${found ? 'good' : 'bad'}`}>
      {found ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
      {sceneName}
      <em>{found ? 'Present' : 'Missing'}</em>
    </span>
  );
}

function ThemeCard({
  theme,
  production,
}: {
  theme: ThemeDiscoveryTheme;
  production: ProductionSnapshot | null;
}) {
  const streamLifecycle = theme.streamLifecycle;

  return (
    <article className="ffs-theme-card">
      <header>
        <div>
          <small>{theme.implementation === 'native' ? 'NATIVE THEME' : 'LEGACY THEME'}</small>
          <h3>{theme.displayName}</h3>
        </div>
        <span className={`ffs-theme-state ${theme.lifecycle}`}>
          {lifecycleLabel(theme.lifecycle)}
        </span>
      </header>

      <div className="ffs-theme-meta">
        <span><strong>ID</strong>{theme.id}</span>
        <span><strong>Selection</strong>{theme.selectable ? 'Selectable' : 'Not selectable'}</span>
      </div>

      <div className="ffs-theme-runtime">
        <div>
          <strong>OBS startup</strong>
          <ScenePresence sceneName={streamLifecycle?.startupSceneName} production={production} />
        </div>
        <div>
          <strong>OBS main</strong>
          <ScenePresence sceneName={streamLifecycle?.mainSceneName} production={production} />
        </div>
        <div>
          <strong>Welcome gate</strong>
          <span>{streamLifecycle ? releaseLabel(streamLifecycle) : 'Not packaged'}</span>
        </div>
      </div>

      <div className="ffs-theme-capabilities">
        {CAPABILITIES.map(({ id, label }) => (
          <div className="ffs-theme-capability" key={id}>
            <span>{label}</span>
            <strong className={`state-${theme.capabilities[id]}`}>
              {capabilityLabel(theme.capabilities[id])}
            </strong>
          </div>
        ))}
      </div>
    </article>
  );
}

export function ThemesPage() {
  const [themes, setThemes] = useState<ThemeDiscoveryTheme[]>([]);
  const [production, setProduction] = useState<ProductionSnapshot | null>(null);
  const [halloweenGhost, setHalloweenGhost] = useState<HalloweenGhostCameraSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [snapshot, productionSnapshot, ghostSnapshot] = await Promise.all([
        loadThemeDiscovery(),
        loadProduction().catch(() => null),
        loadHalloweenGhostCamera().catch(() => null),
      ]);
      setThemes(snapshot.themes);
      setProduction(productionSnapshot);
      setHalloweenGhost(ghostSnapshot);
      setError('');
    } catch (caught) {
      setThemes([]);
      setProduction(null);
      setError(caught instanceof Error ? caught.message : 'Themes could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <div className="ffs-workspace-page ffs-themes-page">
      {error ? <div className="ffs-workspace-message">{error}</div> : null}

      <section className="ffs-workspace-hero">
        <div>
          <small>THEME FRAMEWORK</small>
          <h2>Theme Manager</h2>
          <p>
            Shared theme registration, capability state and server-owned OBS lifecycle.
            Development themes remain unavailable to live stream selection.
          </p>
        </div>
        <button type="button" className="ffs-button secondary" onClick={() => void refresh()} disabled={loading}>
          <RefreshCw size={15} /> Refresh
        </button>
      </section>

      <section className="ffs-workspace-card ffs-theme-framework-note">
        <header>
          <div>
            <small>FRAMEWORK RULE</small>
            <h3>Theme-owned features stay with the theme</h3>
          </div>
        </header>
        <p className="ffs-workspace-note">
          OBS scene names shown here are lifecycle gates only: FFSControl observes them and
          never changes scenes automatically. Theme-specific controls can be added without
          turning them into global features.
        </p>
      </section>

      <div id="theme-halloween" className="ffs-theme-halloween-group">
        <HalloweenGravestoneTestPanel />
        {halloweenGhost ? (
          <HalloweenGhostCameraPanel
            snapshot={halloweenGhost}
            obsOnline={production?.obs.status === 'online'}
            onUpdated={setHalloweenGhost}
          />
        ) : null}
      </div>

      <section id="theme-list" className="ffs-workspace-card ffs-theme-registry-card">
        <header>
          <div>
            <small>REGISTERED THEMES</small>
            <h3>{loading ? 'Loading themes…' : `${themes.length.toLocaleString('en-GB')} themes registered`}</h3>
          </div>
        </header>

        <div className="ffs-theme-grid">
          {themes.map((theme) => <ThemeCard key={theme.id} theme={theme} production={production} />)}
        </div>

        {!loading && themes.length === 0 ? (
          <p className="ffs-workspace-note">No themes were returned by the server registry.</p>
        ) : null}
      </section>


    </div>
  );
}



function HalloweenGravestoneTestPanel() {
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  async function testInObs() {
    setBusy(true);
    setMessage('');
    try {
      await testHalloweenWelcome();
      setMessage('Simulated welcome sent to the connected Halloween gravestone overlay.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Halloween welcome test failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="ffs-workspace-card ffs-halloween-gravestone-tests">
      <header>
        <div>
          <small>HALLOWEEN · PRE-STREAM TESTS</small>
          <h3>Gravestone graphics</h3>
        </div>
      </header>
      <p className="ffs-workspace-note">
        The smaller welcome gravestone rises to the left of the large stone. Preview the two together in a browser,
        or send a simulated welcome to the OBS Halloween browser source with Rehearsal Mode enabled.
        Channel Points redemption and viewer-sound alerts both use the larger gravestone.
      </p>
      <div className="ffs-inline-actions">
        <a className="ffs-button secondary" href="/effects/alerts/halloween?preview=layout" target="_blank" rel="noopener noreferrer">
          Preview Both Stones
        </a>
        <a className="ffs-button secondary" href="/effects/alerts/halloween?preview=redemption" target="_blank" rel="noopener noreferrer">
          Preview Redemption
        </a>
        <a className="ffs-button secondary" href="/effects/alerts/halloween?preview=sound" target="_blank" rel="noopener noreferrer">
          Preview Sound Alert
        </a>
        <button type="button" className="ffs-button primary" disabled={busy} onClick={() => void testInObs()}>
          <Play size={14} /> Test Welcome in OBS
        </button>
      </div>
      {message ? <div className="ffs-workspace-message" role="status">{message}</div> : null}
    </section>
  );
}

function HalloweenGhostCameraPanel(props: {
  snapshot: HalloweenGhostCameraSnapshot;
  obsOnline: boolean;
  onUpdated: (snapshot: HalloweenGhostCameraSnapshot) => void;
}) {
  const [preset, setPreset] = useState<HalloweenGhostPreset>(props.snapshot.settings.preset);
  const [enabled, setEnabled] = useState(props.snapshot.settings.enabled);
  const [wrapperSceneName, setWrapperSceneName] = useState(props.snapshot.settings.wrapperSceneName);
  const [sourceName, setSourceName] = useState(props.snapshot.settings.sourceName ?? '');
  const [opacityPercent, setOpacityPercent] = useState(String(props.snapshot.settings.opacityPercent));
  const [verticalDriftPx, setVerticalDriftPx] = useState(String(props.snapshot.settings.verticalDriftPx));
  const [horizontalDriftPx, setHorizontalDriftPx] = useState(String(props.snapshot.settings.horizontalDriftPx));
  const [scaleDriftPercent, setScaleDriftPercent] = useState(String(props.snapshot.settings.scaleDriftPercent));
  const [verticalPeriodMs, setVerticalPeriodMs] = useState(String(props.snapshot.settings.verticalPeriodMs));
  const [horizontalPeriodMs, setHorizontalPeriodMs] = useState(String(props.snapshot.settings.horizontalPeriodMs));
  const [scalePeriodMs, setScalePeriodMs] = useState(String(props.snapshot.settings.scalePeriodMs));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const settings = props.snapshot.settings;
    setPreset(settings.preset);
    setEnabled(settings.enabled);
    setWrapperSceneName(settings.wrapperSceneName);
    setSourceName(settings.sourceName ?? '');
    setOpacityPercent(String(settings.opacityPercent));
    setVerticalDriftPx(String(settings.verticalDriftPx));
    setHorizontalDriftPx(String(settings.horizontalDriftPx));
    setScaleDriftPercent(String(settings.scaleDriftPercent));
    setVerticalPeriodMs(String(settings.verticalPeriodMs));
    setHorizontalPeriodMs(String(settings.horizontalPeriodMs));
    setScalePeriodMs(String(settings.scalePeriodMs));
  }, [props.snapshot]);

  async function apply(changes: Partial<HalloweenGhostCameraSnapshot['settings']>) {
    setBusy(true);
    try {
      const updated = await updateHalloweenGhostCamera(changes);
      props.onUpdated(updated);
      setMessage('Halloween Ghost Camera settings saved.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Halloween Ghost Camera update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function saveCustom() {
    const numbers = {
      opacityPercent: Number(opacityPercent),
      verticalDriftPx: Number(verticalDriftPx),
      horizontalDriftPx: Number(horizontalDriftPx),
      scaleDriftPercent: Number(scaleDriftPercent),
      verticalPeriodMs: Number(verticalPeriodMs),
      horizontalPeriodMs: Number(horizontalPeriodMs),
      scalePeriodMs: Number(scalePeriodMs),
    };
    if (
      !Number.isFinite(numbers.opacityPercent) ||
      !Number.isFinite(numbers.verticalDriftPx) ||
      !Number.isFinite(numbers.horizontalDriftPx) ||
      !Number.isFinite(numbers.scaleDriftPercent) ||
      !Number.isFinite(numbers.verticalPeriodMs) ||
      !Number.isFinite(numbers.horizontalPeriodMs) ||
      !Number.isFinite(numbers.scalePeriodMs)
    ) {
      setMessage('Enter valid Ghost Camera numbers.');
      return;
    }
    await apply({
      preset: 'custom',
      enabled,
      wrapperSceneName,
      sourceName: sourceName.trim() || null,
      ...numbers,
    });
  }

  return (
    <section className="ffs-workspace-card ffs-halloween-ghost-card">
      <header>
        <div>
          <small>HALLOWEEN · LIVE</small>
          <h3>Ghost Camera</h3>
        </div>
        <span className={`ffs-theme-state ${props.snapshot.runtime.applied ? 'ready' : 'development'}`}>
          {props.snapshot.runtime.applied ? 'Applied' : 'Not applied'}
        </span>
      </header>

      <p className="ffs-workspace-note">
        FFSControl owns a wrapper-only camera treatment. It never changes the active OBS scene and
        never modifies the normal camera transform. Select Halloween on Start Stream to use the
        HW Startup / HW Multi lifecycle; the ghost treatment applies only when its configured
        wrapper/source is available on the Stream PC.
      </p>

      <div className="ffs-halloween-status-grid">
        <div><span>OBS</span><strong>{props.obsOnline ? 'Online' : 'Offline / unavailable'}</strong></div>
        <div><span>Effective theme</span><strong>{props.snapshot.runtime.effectiveTheme ?? 'None'}</strong></div>
        <div><span>Last action</span><strong>{props.snapshot.runtime.lastActionAt ? new Date(props.snapshot.runtime.lastActionAt).toLocaleString('en-GB') : 'None'}</strong></div>
      </div>

      {props.snapshot.runtime.lastError ? <div className="ffs-workspace-message">{props.snapshot.runtime.lastError}</div> : null}

      <div className="ffs-halloween-presets">
        {(['off', 'subtle', 'strong'] as HalloweenGhostPreset[]).map((value) => (
          <button
            key={value}
            type="button"
            className={`ffs-button ${preset === value ? 'primary' : 'secondary'}`}
            disabled={busy}
            onClick={() => void apply({ preset: value })}
          >
            {value === 'off' ? 'Off' : value === 'subtle' ? 'Subtle' : 'Strong'}
          </button>
        ))}
      </div>

      <div className="ffs-halloween-form-grid">
        <label>
          <span>Wrapper scene</span>
          <input value={wrapperSceneName} maxLength={256} onChange={(event) => setWrapperSceneName(event.target.value)} />
        </label>
        <label>
          <span>Camera source inside wrapper</span>
          <input value={sourceName} maxLength={256} placeholder="Set when OBS is available" onChange={(event) => setSourceName(event.target.value)} />
        </label>
        <label>
          <span>Opacity</span>
          <div className="ffs-suffix-input"><input type="number" min="50" max="100" step="1" value={opacityPercent} onChange={(event) => setOpacityPercent(event.target.value)} /><b>%</b></div>
        </label>
        <label>
          <span>Vertical drift</span>
          <div className="ffs-suffix-input"><input type="number" min="0" max="50" step="1" value={verticalDriftPx} onChange={(event) => setVerticalDriftPx(event.target.value)} /><b>px</b></div>
        </label>
        <label>
          <span>Horizontal drift</span>
          <div className="ffs-suffix-input"><input type="number" min="0" max="30" step="1" value={horizontalDriftPx} onChange={(event) => setHorizontalDriftPx(event.target.value)} /><b>px</b></div>
        </label>
        <label>
          <span>Scale drift</span>
          <div className="ffs-suffix-input"><input type="number" min="0" max="2" step="0.1" value={scaleDriftPercent} onChange={(event) => setScaleDriftPercent(event.target.value)} /><b>%</b></div>
        </label>
        <label>
          <span>Vertical period</span>
          <div className="ffs-suffix-input"><input type="number" min="3000" max="30000" step="500" value={verticalPeriodMs} onChange={(event) => setVerticalPeriodMs(event.target.value)} /><b>ms</b></div>
        </label>
        <label>
          <span>Horizontal period</span>
          <div className="ffs-suffix-input"><input type="number" min="3000" max="30000" step="500" value={horizontalPeriodMs} onChange={(event) => setHorizontalPeriodMs(event.target.value)} /><b>ms</b></div>
        </label>
        <label>
          <span>Scale period</span>
          <div className="ffs-suffix-input"><input type="number" min="3000" max="30000" step="500" value={scalePeriodMs} onChange={(event) => setScalePeriodMs(event.target.value)} /><b>ms</b></div>
        </label>
      </div>

      <div className="ffs-inline-actions">
        <label className="ffs-halloween-enabled">
          <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
          Ghost Camera enabled
        </label>
        <button type="button" className="ffs-button primary" disabled={busy} onClick={() => void saveCustom()}>
          <Save size={14} /> Save Custom
        </button>
      </div>
      {message ? <div className="ffs-workspace-message">{message}</div> : null}
    </section>
  );
}
